// Shared copilot sidebar shell for analytics chat (flow and case).
// Domain-specific config (chat hook, prompts, labels) is passed as props.

import { useCallback, useEffect, useRef, useState } from "react";

import { times } from "lodash-es";
import { m } from "motion/react";

import { CopilotSidebarHeader } from "src/analytics/copilot/CopilotSidebarHeader";
import {
  type DashboardPinning,
  LiveAnalyticsMessages,
} from "src/analytics/copilot/LiveAnalyticsMessages";
import type { AnalyticsChatResult } from "src/analytics/copilot/useAnalyticsChatSession";
import {
  PinChartModal,
  type PinModalData,
} from "src/analytics/dashboard/PinChartModal";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatMessageSkeleton } from "src/base-components/Chat/ChatMessageSkeleton";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { ResizeHandleButton } from "src/base-components/ResizableSidepane";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { SandboxedChart } from "src/chartSandbox/SandboxedChart";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { PromptBox } from "src/copilot/PromptBox";
import { useModal } from "src/design-system/Modal";

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

type AnalyticsCopilotSidebarProps = {
  onClose: () => void;
  chat: AnalyticsChatResult;
  labelDictionaries: LabelDictionaries;
  dataSource?: string;
  pinning?: DashboardPinning;
  storageKey: string;
  dataLoc: string;
  emptyStateDataLoc: string;
  suggestionPrompts: ChatEmptyStatePrompt[];
  promptBoxTopContent?: React.ReactNode;
  submitLabel?: string;
};

export const AnalyticsCopilotSidebar: React.FC<
  AnalyticsCopilotSidebarProps
> = ({
  onClose,
  chat,
  labelDictionaries,
  dataSource,
  pinning,
  storageKey,
  dataLoc,
  emptyStateDataLoc,
  suggestionPrompts,
  promptBoxTopContent,
  submitLabel,
}) => {
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: 528,
  });

  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const shouldAutoScrollRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    shouldAutoScrollRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 50;
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (el && shouldAutoScrollRef.current) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
  }, []);

  const [draft, setDraft] = useState("");
  const {
    isActive,
    isProvisioning,
    isReady,
    isExpired,
    failureMessage,
    isRestoringSession,
    messages,
    send,
    resetSession,
  } = chat;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, isActive, failureMessage, scrollToBottom]);

  const busy = isActive || !isReady || isExpired;

  const handleSubmit = useCallback(
    (message: string) => {
      if (!message.trim() || busy) return;
      setDraft("");
      send(message);
    },
    [busy, send],
  );

  const handleReset = useCallback(() => {
    setDraft("");
  }, []);

  const {
    isOpen: isPinModalOpen,
    openModal: openPinModal,
    closeModal: closePinModal,
    afterLeave: afterPinModalLeave,
    data: pinData,
  } = useModal<PinModalData>();

  const handlePinConfirm = (title: string) => {
    if (pinData && pinning) {
      pinning.pinChart(
        {
          type: "chart",
          query: { query: pinData.sql, parameters: pinData.parameterNames },
          chart_definition: pinData.chartDefinition,
        },
        pinData.pinId,
        title,
      );
    }
    closePinModal();
  };

  return (
    <FloatingWindowsProvider>
      <m.div
        className="shrink-0 overflow-hidden"
        style={{ width }}
        initial={false}
        animate={{ width }}
        exit={{ width: 0 }}
        transition={{
          duration: isResizing ? 0 : 0.2,
          ease: "easeOut",
        }}
      >
        <aside
          className="relative flex h-full max-h-screen flex-col border-gray-200 border-l bg-white"
          data-loc={dataLoc}
          style={{ width }}
        >
          <ResizeHandleButton onResizeStart={handleResizeStart} />

          <div className="shrink-0 px-4">
            <CopilotSidebarHeader
              isNewConversation={messages.length === 0 && !failureMessage}
              onClose={onClose}
              onNewConversation={resetSession}
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            <ChatBody>
              <ChatMessageList ref={scrollContainerRef}>
                {isRestoringSession ? (
                  times(3, (i) => (
                    <ChatMessageSkeleton
                      key={i}
                      dataLoc="analytics-restore-skeleton"
                    />
                  ))
                ) : messages.length === 0 && !isActive && !failureMessage ? (
                  <ChatEmptyState
                    dataLoc={emptyStateDataLoc}
                    prompts={suggestionPrompts}
                    variant="new"
                    onPromptClick={handleSubmit}
                  />
                ) : (
                  <LiveAnalyticsMessages
                    dataSource={dataSource}
                    failureMessage={failureMessage}
                    isActive={isActive}
                    isProvisioning={isProvisioning}
                    labelDictionaries={labelDictionaries}
                    messages={messages}
                    onRequestPin={pinning ? openPinModal : undefined}
                    pinning={pinning}
                  />
                )}
              </ChatMessageList>

              <ChatInputArea>
                <PromptBox
                  disabled={busy || draft.trim().length === 0}
                  errored={false}
                  mentionTextPlaceholder=""
                  minified={false}
                  resource={[]}
                  resourceType={
                    FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
                  }
                  submitLabel={submitLabel}
                  topContent={promptBoxTopContent}
                  value={draft}
                  onChange={setDraft}
                  onReset={handleReset}
                  onSubmit={(content) => handleSubmit(content)}
                />
              </ChatInputArea>
            </ChatBody>
          </div>
        </aside>
      </m.div>
      <PinChartModal
        afterLeave={afterPinModalLeave}
        defaultTitle={pinData?.defaultTitle ?? ""}
        open={isPinModalOpen}
        onClose={closePinModal}
        onConfirm={handlePinConfirm}
      >
        {pinData && (
          <SandboxedChart
            chartDefinition={pinData.chartDefinition}
            labelDictionaries={pinData.labelDictionaries}
            rows={pinData.rows}
          />
        )}
      </PinChartModal>
    </FloatingWindowsProvider>
  );
};
